import React, { useReducer } from 'react';
import PropTypes from 'prop-types';
import Button from '@strava/ui/Button';
import I18n from 'utils/I18n';
import useFeedAnalytics from '../../hooks/useFeedAnalytics';
import {
  pageProps,
  viewingAthleteProps,
  cursorDataProps,
  feedTypeProps,
  indexProps
} from '../../props/feed-prop-types';
import { init, reducer } from '../../state/feed-entity-reducer';
import { buildInitialState } from '../../state/feed-entity-init-state';

import styles from './index.scss';

const RunningRaceJoined = ({
  entity,
  viewingAthlete,
  clubId,
  page,
  feedType,
  index,
  cursorData,
  rowData,
  athleteProfileId
}) => {
  const { trackAnalytics, intersectionObserverRef } = useFeedAnalytics({
    page,
    entity,
    entityId: rowData.goal_entries[0].entity_id,
    athleteProfileId,
    feedType,
    index,
    viewingAthleteId: viewingAthlete.id,
    clubId
  });

  const [entityState] = useReducer(
    reducer,
    buildInitialState({
      trackAnalytics
    }),
    init
  );

  const isJoined = rowData.current_athlete_joined;
  const cardMargin = index > 0 ? styles.cardMargin : '';
  const buttonLabelAndVariant = isJoined
    ? [I18n.t('components.feed.running_race_joined.view'), 'default']
    : [I18n.t('components.feed.running_race_joined.join'), 'primary'];

  const onCTAButtonClick = () => {
    if (isJoined) {
      entityState.trackAnalytics({
        action: 'click',
        element: `view_${entity.toLowerCase()}`
      });
    } else {
      entityState.trackAnalytics({
        action: 'click',
        element: `join_${entity.toLowerCase()}`
      });
    }
    window.location.href = rowData.race_url;
  };

  return (
    <div
      className={`react-feed-entry ${styles.feedEntry} ${styles.card} ${cardMargin}`}
      data-updated-at={cursorData.updated_at}
      data-rank={cursorData.rank}
      data-testid="web-feed-entry"
      ref={intersectionObserverRef}
    >
      <div className={styles.entryHeader}>
        <div className={styles.entryOwner}>{rowData.title}</div>
      </div>

      <div className={styles.entryBody}>
        <div className={styles.media}>
          {/* Left Logo */}
          <div className={styles.mediaLeft}>
            <img
              data-testid="race-logo"
              className={styles.logo}
              src="/images/compete/race-entity-90x90.png"
              alt={I18n.t('components.feed.running_race_joined.logo')}
            />
          </div>

          {/* Right Main Body */}
          <div className={styles.mediaBody}>
            <h3 className={styles.titleText}>
              <a
                href={rowData.race_url}
                onClick={() => {
                  entityState.trackAnalytics({
                    action: 'click',
                    element: `${entity.toLowerCase()}_headline_and_description`
                  });
                }}
              >
                {I18n.t('components.feed.running_race_joined.year_and_name', {
                  year: rowData.race_year,
                  name: rowData.race_name
                })}
              </a>
            </h3>

            <Button
              onClick={onCTAButtonClick}
              variant={buttonLabelAndVariant[1]}
              className={styles.ctaBtn}
              type="button"
            >
              {buttonLabelAndVariant[0]}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

RunningRaceJoined.propTypes = {
  entity: PropTypes.oneOf(['RunningRaceJoined']).isRequired,
  viewingAthlete: viewingAthleteProps.isRequired,
  clubId: PropTypes.string.isRequired,
  athleteProfileId: PropTypes.string.isRequired,
  page: pageProps.isRequired,
  cursorData: cursorDataProps.isRequired,
  feedType: feedTypeProps.isRequired,
  index: indexProps.isRequired,
  rowData: PropTypes.shape({
    title: PropTypes.string.isRequired,
    current_athlete_joined: PropTypes.bool.isRequired,
    race_url: PropTypes.string.isRequired,
    race_year: PropTypes.string.isRequired,
    race_name: PropTypes.string.isRequired,
    goal_entries: PropTypes.arrayOf(
      PropTypes.shape({
        shareable: PropTypes.bool,
        is_boosted: PropTypes.bool,
        twitter_url: PropTypes.string.isRequired,
        entity_id: PropTypes.number.isRequired,
        description: PropTypes.string.isRequired,
        athlete_feed_entry_id: PropTypes.number.isRequired,
        athlete_id: PropTypes.number.isRequired
      })
    )
  }).isRequired
};

export default RunningRaceJoined;
